const getDefaultWindow = () => (typeof window !== 'undefined' ? window : null);
const getDefaultDocument = () =>
  typeof document !== 'undefined' ? document : null;
const getDefaultCustomEvent = () =>
  typeof CustomEvent !== 'undefined' ? CustomEvent : null;

const addSafeEventListener = (
  target = null,
  eventName = '',
  handler = null,
  options = undefined
) => {
  if (
    !eventName ||
    typeof handler !== 'function' ||
    typeof target?.addEventListener !== 'function'
  ) {
    return false;
  }

  try {
    target.addEventListener(eventName, handler, options);
    return true;
  } catch (_) {
    return false;
  }
};

const addSafeWindowEventListener = (
  eventName = '',
  handler = null,
  options = undefined
) => addSafeEventListener(getDefaultWindow(), eventName, handler, options);

const addSafeDocumentEventListener = (
  eventName = '',
  handler = null,
  options = undefined
) => addSafeEventListener(getDefaultDocument(), eventName, handler, options);

const removeSafeEventListener = (
  target = null,
  eventName = '',
  handler = null,
  options = undefined
) => {
  if (
    !eventName ||
    typeof handler !== 'function' ||
    typeof target?.removeEventListener !== 'function'
  ) {
    return false;
  }

  try {
    target.removeEventListener(eventName, handler, options);
    return true;
  } catch (_) {
    return false;
  }
};

const removeSafeWindowEventListener = (
  eventName = '',
  handler = null,
  options = undefined
) => removeSafeEventListener(getDefaultWindow(), eventName, handler, options);

const removeSafeDocumentEventListener = (
  eventName = '',
  handler = null,
  options = undefined
) => removeSafeEventListener(getDefaultDocument(), eventName, handler, options);

const createSafeCustomEvent = (
  eventName = '',
  detail = undefined,
  {
    customEventConstructor: CustomEventConstructor = getDefaultCustomEvent(),
    documentObject = getDefaultDocument(),
  } = {}
) => {
  if (!eventName) {
    return null;
  }

  try {
    if (typeof CustomEventConstructor === 'function') {
      return new CustomEventConstructor(eventName, { detail });
    }

    if (typeof documentObject?.createEvent === 'function') {
      const event = documentObject.createEvent('CustomEvent');
      event.initCustomEvent(eventName, false, false, detail);
      return event;
    }
  } catch (_) {
    return null;
  }

  return null;
};

const dispatchSafeCustomEvent = (
  target = getDefaultWindow(),
  eventName = '',
  detail = undefined,
  {
    customEventConstructor: CustomEventConstructor = getDefaultCustomEvent(),
  } = {}
) => {
  if (!eventName || typeof target?.dispatchEvent !== 'function') {
    return false;
  }

  try {
    const event = createSafeCustomEvent(eventName, detail, {
      customEventConstructor: CustomEventConstructor,
    });

    if (!event) {
      return false;
    }

    return Boolean(target.dispatchEvent(event));
  } catch (_) {
    return false;
  }
};

const dispatchSafeWindowCustomEvent = (
  eventName = '',
  detail = undefined,
  options = {}
) => dispatchSafeCustomEvent(getDefaultWindow(), eventName, detail, options);

export {
  addSafeDocumentEventListener,
  addSafeEventListener,
  addSafeWindowEventListener,
  createSafeCustomEvent,
  dispatchSafeCustomEvent,
  dispatchSafeWindowCustomEvent,
  removeSafeDocumentEventListener,
  removeSafeEventListener,
  removeSafeWindowEventListener,
};
